import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Palmtree } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/status/${sid}`);
        setBooking(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) {
        console.error("Payment status check failed:", e);
      }
      attempts.current += 1;
      if (attempts.current < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sp]);

  return (
    <div className="section-sand min-h-[70vh] flex items-center justify-center py-16 px-4">
      <div className="bg-white rounded-3xl shadow-card border border-border p-10 max-w-md w-full text-center animate-fade-up">
        {status === "checking" && <p className="text-canard font-body py-10">{t("payment.processing")}</p>}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-emeraude/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-11 h-11 text-emeraude" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl text-ink">{t("payment.success")}</h1>
            <p className="text-muted2 font-body mt-3">{t("payment.successText")}</p>
            <div className="bg-sand rounded-xl p-4 mt-6 text-left text-sm font-body space-y-1">
              <p><span className="text-muted2">{t("payment.ref")}:</span> <span className="font-bold text-canard-dark">{booking?.ref}</span></p>
              <p><span className="text-muted2">{t("nav.vehicles")}:</span> <span className="font-bold">{booking?.vehicle_name}</span></p>
              <p><span className="text-muted2">{t("checkout.total")}:</span> <span className="font-bold">{booking?.amount} €</span></p>
              <p><span className="text-muted2">{t("vehicle.deposit")}:</span> <span className="font-bold">{booking?.deposit} €</span></p>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => nav("/")} className="flex-1 rounded-full">{t("payment.backHome")}</Button>
              <Button data-testid="view-bookings-button" onClick={() => nav("/my/bookings")} className="flex-1 rounded-full bg-canard hover:bg-canard-dark">{t("payment.viewBookings")}</Button>
            </div>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <Palmtree className="w-12 h-12 text-canard mx-auto mb-4" />
            <p className="text-muted2 font-body">{t("payment.processing")} {booking?.ref}</p>
            <Button onClick={() => nav("/my/bookings")} className="mt-6 rounded-full bg-canard">{t("payment.viewBookings")}</Button>
          </>
        )}
      </div>
    </div>
  );
}
